import React, { memo, useMemo } from 'react';

import { Stack } from '@/components/layout';
import { Avatar, Text } from '@/components/ui';
import { createStyles, useScaling } from '@/hooks';
import { guardianColors } from '@/theme/colors';
import type { FamilyActivityItem } from './types';
import { buildActivityDescriptionSegments, formatActivityTime } from './utils';

// Line height of the message text ("Subtitle 14"); the time label matches it so its
// glyph sits on the first line instead of hanging above a taller line.
const MESSAGE_LINE_HEIGHT = 19.6;

type FamilyActivityRowProps = {
  item: FamilyActivityItem;
};

function FamilyActivityRow({ item }: FamilyActivityRowProps) {
  const { scale } = useScaling();
  const styles = useStyles();
  // Read fresh at render time (not threaded from a parent-computed value) so the label
  // reflects "now" whenever this row actually re-renders.
  const timeLabel = formatActivityTime(item.createdAt);

  const segments = useMemo(
    () => buildActivityDescriptionSegments(item.description, [item.actorName]),
    [item.description, item.actorName],
  );

  return (
    <Stack direction="row" justify="center" style={styles.root} gap={scale(8)}>
      <Avatar size={scale(32)} borderWidth={0} activeOpacity={1}>
        <Avatar.Image source={item.actorAvatar} backgroundColor={guardianColors['2']} />
      </Avatar>

      <Stack flex={1} justify="center">
        <Text variant="Font Guardian App/Subtitle/Subtitle 14 Regular" style={styles.message}>
          {segments.map((segment, index) =>
            segment.bold ? (
              <Text
                key={index}
                variant="Font Guardian App/Subtitle/Subtitle 14 SemiBold"
                style={styles.name}>
                {segment.text}
              </Text>
            ) : (
              segment.text
            ),
          )}
        </Text>
      </Stack>

      <Text
        variant="Font Guardian App/Subtitle/Subtitle 12 Regular"
        lineHeight={scale(MESSAGE_LINE_HEIGHT)}
        style={styles.time}>
        {timeLabel}
      </Text>
    </Stack>
  );
}

const useStyles = createStyles(({ scale }) => ({
  root: {
    paddingHorizontal: scale(12),
    paddingVertical: scale(8),
  },
  message: {
    color: guardianColors['10'],
  },
  name: {
    color: guardianColors['10'],
  },
  time: {
    color: guardianColors['6'],
  },
}));

export default memo(FamilyActivityRow);
